/**
 * Utility to apply white-label navigation configuration to sidebar config
 * This filters and reorders sidebar sections based on white-label settings
 */

interface NavigationItem {
  id: string;
  visible: boolean;
  order: number;
}

interface WhiteLabelNavigationConfig {
  items: NavigationItem[];
}

// Map sidebar config keys to navigation section IDs
const SECTION_ID_MAP: Record<string, string> = {
  'home': 'section-home',
  'policyManagement': 'section-policy-management',
  'managementSystem': 'section-management-system',
  'reporting': 'section-reporting',
  'adminTools': 'section-admin-tools',
  'navSecondary': 'section-nav-secondary',
};

// Reverse map for looking up config key from section ID
const ID_TO_SECTION_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(SECTION_ID_MAP).map(([key, value]) => [value, key])
);

// Map item route names/URLs to navigation item IDs
// This helps us identify which navigation config applies to which sidebar item
const ITEM_ID_PATTERNS: Record<string, RegExp | string> = {
  'home-dashboard': /^\/dashboard$/,
  'policy-admin': /^\/policyadmin$/,
  'policy-reader': /^\/policy-reader$/,
  'mgmt-compliance-calendar': /^\/compliance-calendar$/,
  'mgmt-assurance-program': /^\/assurancelibrary$/,
  'mgmt-assurance-tasks': /^\/assurancelibrary$/,
  'mgmt-audit-centre': /^\/auditcentre/,
  'mgmt-registers': /^\/registers$/,
  'mgmt-key-documents': /^\/key-documents$/,
  'reporting-library': /^\/report-library$/,
  'reporting-auditor-pack': 'auditor-pack',
  'admin-user-management': /^\/users$/,
  'admin-company-settings': /^\/settings$/,
  'nav-knowledge-base': 'knowledge-base',
  'nav-help': 'help',
  'nav-feedback': 'feedback',
};

/**
 * Try to match a sidebar item to a navigation item ID
 */
function matchItemToNavId(item: any): string | null {
  if (!item) return null;
  
  const url = item.url || '';
  const routeName = item.routeName || '';
  const title = item.title || '';
  
  for (const [navId, pattern] of Object.entries(ITEM_ID_PATTERNS)) {
    if (pattern instanceof RegExp) {
      if (pattern.test(url) || pattern.test(routeName)) {
        return navId;
      }
    } else if (typeof pattern === 'string') {
      if (url.includes(pattern) || routeName.includes(pattern) || title.toLowerCase().includes(pattern)) {
        return navId;
      }
    }
  }
  
  return null;
}

/**
 * Apply white-label navigation configuration to sidebar config
 * @param sidebarConfig The original sidebar configuration from getSidebarConfig
 * @param whiteLabelNav The white-label navigation configuration
 * @returns Modified sidebar config with filtered and reordered sections and items
 */
export function applyWhiteLabelNavigation(
  sidebarConfig: any,
  whiteLabelNav?: WhiteLabelNavigationConfig
): any {
  // If no white-label nav config, return original
  if (!whiteLabelNav?.items || whiteLabelNav.items.length === 0) {
    return sidebarConfig;
  }

  // Create a map of navigation items by ID
  const navMap = new Map(whiteLabelNav.items.map(item => [item.id, item]));

  // Get section visibility and order
  const sectionConfigs = Object.entries(SECTION_ID_MAP).map(([configKey, sectionId]) => {
    const navItem = navMap.get(sectionId);
    const sectionItems = sidebarConfig[configKey] || [];
    
    // Apply item-level filtering and reordering
    const processedItems = sectionItems
      .map((item: any) => {
        const navId = matchItemToNavId(item);
        const itemNav = navId ? navMap.get(navId) : null;
        
        return {
          ...item,
          _navId: navId,
          _visible: itemNav?.visible ?? true,
          _order: itemNav?.order ?? item.order ?? 0,
        };
      })
      .filter((item: any) => item._visible)
      .sort((a: any, b: any) => a._order - b._order)
      .map((item: any) => {
        // Remove temporary properties
        const { _navId, _visible, _order, ...cleanItem } = item;
        return cleanItem;
      });
    
    return {
      configKey,
      sectionId,
      visible: navItem?.visible ?? true,
      order: navItem?.order ?? 0,
      items: processedItems,
    };
  });

  // Filter visible sections and sort by order
  const visibleSections = sectionConfigs
    .filter(section => section.visible && section.items.length > 0)
    .sort((a, b) => a.order - b.order);

  // Build new sidebar config with reordered sections and items
  const newConfig: any = {
    user: sidebarConfig.user,
  };

  // Initialize all sections as empty arrays
  Object.keys(SECTION_ID_MAP).forEach(key => {
    newConfig[key] = [];
  });

  // Populate sections in order with reordered items
  visibleSections.forEach(section => {
    newConfig[section.configKey] = section.items;
  });

  return newConfig;
}

/**
 * Get the display order of sections for rendering
 * @param whiteLabelNav The white-label navigation configuration
 * @returns Array of config keys in display order
 */
export function getSectionDisplayOrder(whiteLabelNav?: WhiteLabelNavigationConfig): string[] {
  if (!whiteLabelNav?.items || whiteLabelNav.items.length === 0) {
    // Default order
    return ['home', 'policyManagement', 'managementSystem', 'reporting', 'adminTools', 'navSecondary'];
  }

  const navMap = new Map(whiteLabelNav.items.map(item => [item.id, item]));

  const sectionOrder = Object.entries(SECTION_ID_MAP)
    .map(([configKey, sectionId]) => {
      const navItem = navMap.get(sectionId);
      return {
        configKey,
        visible: navItem?.visible ?? true,
        order: navItem?.order ?? 0,
      };
    })
    .filter(section => section.visible)
    .sort((a, b) => a.order - b.order)
    .map(section => section.configKey);

  return sectionOrder;
}
